import { useEffect, useMemo, useRef } from 'react';

import { useOverlayScrollbars } from 'overlayscrollbars-react';
import { ScrollerProps } from 'react-virtuoso';

import { setRef } from '@cast/utils';

import { WithRef } from '../../../types';
import { SCROLLER_THEME } from '../../scroller';
import { useTableContext } from '../hooks';

export const TableScroller = ({
  ref,
  style,
  children,
  ...rest
}: WithRef<HTMLDivElement, ScrollerProps>) => {
  const ctx = useTableContext()!;
  const {
    rootRef: tableRef,
    api: {
      current: {
        columnsInfo: { floatingColumn },
      },
    },
    isFloatingColumnSticky,
    setIsFloatingColumnSticky,
  } = ctx;

  const [groupHeaders, headers] = useMemo(() => {
    if (tableRef) {
      return [
        tableRef.querySelector('[data-group-headers]'),
        tableRef.querySelector('[data-headers]'),
      ];
    }
    return [];
  }, [tableRef]);

  const [initialize, instance] = useOverlayScrollbars({
    events: {
      scroll: (_, { target }) => {
        const { scrollLeft, scrollWidth, clientWidth } =
          target as HTMLDivElement;

        if (floatingColumn) {
          const activateFloatingColumn =
            scrollLeft -
              scrollWidth +
              clientWidth +
              floatingColumn.computedWidth <
            0;
          if (activateFloatingColumn !== isFloatingColumnSticky) {
            setIsFloatingColumnSticky(activateFloatingColumn);
          }
        }
        // Preventing anything more abstract, such as generic selector and looping through HTMLCollection to be as performant, as possible
        if (groupHeaders) {
          groupHeaders.scrollLeft = scrollLeft;
        }
        if (headers) {
          headers.scrollLeft = scrollLeft;
        }
        tableRef?.style.setProperty('--scrollLeft', `${scrollLeft}px`);
      },
    },
    options: {
      /**
       * When the screen is quickly resized, `overlayScrollars` triggers `scroll` event,
       * and the event handler, `scrollWidth` and `clientWidth` sometimes are out-of-sync.
       * That causes the floating column to enter the floating state when it shouldn't.
       * Adding `debounce: 1` delays calling the `scroll` handler to the next vm turn, when values are already in-sync.
       */
      update: { debounce: 1 },
      showNativeOverlaidScrollbars: false,
      scrollbars: {
        autoHide: 'move',
        theme: SCROLLER_THEME,
      },
    },
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    initialize({
      target: rootRef.current!,
      elements: {
        viewport: contentRef.current,
        content: contentRef.current,
      },
    });
  }, [initialize, contentRef, rootRef]);

  const resizedColumns = ctx.api.current.state.columnSettings.resizedColumns;

  useEffect(() => {
    // Recalculate scrollbars when columns are resized
    instance()?.update(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizedColumns]);

  return (
    <div
      ref={(el) => {
        if (el) {
          rootRef.current = el;
        }
      }}
      className="DS-Table-Scroller"
      style={{
        height: style?.height,
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        ref={(el) => {
          if (el) {
            contentRef.current = el;
            setRef(ref, el);
          }
        }}
        {...rest}
      >
        {children}
      </div>
    </div>
  );
};
TableScroller.displayName = 'TableScroller';
